import { 
  Package, 
  Gauge, 
  MapPin, 
  Heart, 
  Wind, 
  LayoutGrid,
  ArrowRight
} from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';
import { Link } from 'react-router-dom';

// Custom images provided by user
const IMAGES = {
  lockers: "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/b8wuhv22_Smart_lockers.jpeg",
  metering: "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/lyivb3qq_Energy_Monitoring.JPG",
  tracking: "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/nv8lrlxj_Asset_Tracking.jpg",
  airQuality: "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/2cn0jbjr_Indoor_Air_quality_monitoring.jpg",
  infrastructure: "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/eu41x4b4_Solutions%20for%20Infrastructures.webp",
  elderlyCare: "https://customer-assets.emergentagent.com/job_industrial-iot-4/artifacts/gzbm5kwl_happy-selfie-and-senior-friends-in-a-park-while-w-2026-01-09-09-41-23-utc.jpg"
};

export const SolutionsSection = () => {
  const { t, language } = useLanguage();

  // Get Eldera link based on current language
  const getElderaLink = () => {
    if (language === 'lv') return 'https://eldera.lv';
    if (language === 'lt') return 'https://eldera.lt';
    return null; // No link for other languages
  };

  const solutions = [
    {
      id: 'smart-lockers',
      title: t('solutions.smartLockers'),
      description: t('solutions.smartLockersDesc'),
      icon: Package,
      image: IMAGES.lockers,
      size: 'wide',
      link: '/smart-lockers',
    },
    {
      id: 'smart-metering',
      title: t('solutions.smartMetering'),
      description: t('solutions.smartMeteringDesc'),
      icon: Gauge,
      image: IMAGES.metering,
      size: 'square',
      link: '/smart-metering',
    },
    {
      id: 'asset-tracking',
      title: t('solutions.assetTracking'),
      description: t('solutions.assetTrackingDesc'),
      icon: MapPin,
      image: IMAGES.tracking,
      size: 'tall',
      link: '/asset-tracking',
    },
    {
      id: 'elderly-care',
      title: t('solutions.elderlyCare'),
      description: t('solutions.elderlyCareDesc'),
      icon: Heart,
      image: IMAGES.elderlyCare,
      size: 'square',
      externalLink: getElderaLink(),
    },
    {
      id: 'air-quality',
      title: t('solutions.airQuality'),
      description: t('solutions.airQualityDesc'),
      icon: Wind,
      image: IMAGES.airQuality,
      size: 'square',
      link: '/indoor-air-quality-monitoring',
    },
    {
      id: 'smart-office',
      title: t('solutions.smartOffice'),
      description: t('solutions.smartOfficeDesc'),
      icon: LayoutGrid,
      image: IMAGES.infrastructure,
      size: 'wide',
      link: '/iot-for-cities',
    },
  ];

  return (
    <section id="solutions" className="py-20 md:py-32 bg-slate-900" data-testid="solutions-section">
      <div className="container-0g">
        {/* Section Header */}
        <div className="mb-12 md:mb-16">
          <p className="text-xs font-bold uppercase tracking-widest text-teal-400 mb-4">{t('solutions.label')}</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white mb-4">{t('solutions.title')}</h2>
          <p className="text-lg text-slate-400 max-w-2xl">
            {t('solutions.subtitle')}
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {solutions.map((solution) => {
            const Icon = solution.icon;
            const isWide = solution.size === 'wide';
            const isTall = solution.size === 'tall';
            
            const cardContent = (
              <>
                {/* Background Image (if available) */}
                {solution.image && (
                  <div className="absolute inset-0">
                    <img 
                      src={solution.image} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                    {/* Overlay for text readability */}
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/80 to-slate-900/50" />
                  </div>
                )}
                
                <div className="relative z-10">
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-sm bg-teal-600/20 flex items-center justify-center mb-4 group-hover:bg-teal-600 transition-colors">
                    <Icon className="h-6 w-6 text-teal-400 group-hover:text-white transition-colors" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-semibold text-white mb-2">
                    {solution.title}
                  </h3>
                  <p className="text-slate-400 mb-4">
                    {solution.description}
                  </p>
                  
                  {/* Link */}
                  <div className="flex items-center text-teal-400 font-medium text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    <span>{t('solutions.learnMore')}</span>
                    <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </>
            );

            const cardClasses = `
              solution-card group cursor-pointer relative overflow-hidden bg-slate-800 border border-slate-700 p-6 rounded-sm
              hover:border-teal-500 transition-colors
              ${isWide ? 'md:col-span-2 lg:col-span-2' : ''}
              ${isTall ? 'md:row-span-2' : ''}
            `;

            // If solution has an internal link, wrap in Link component
            if (solution.link) {
              return (
                <Link
                  key={solution.id}
                  to={solution.link}
                  className={cardClasses}
                  data-testid={`solution-${solution.id}`}
                >
                  {cardContent}
                </Link>
              );
            }

            // If solution has an external link, wrap in anchor tag
            if (solution.externalLink) {
              return (
                <a
                  key={solution.id}
                  href={solution.externalLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClasses}
                  data-testid={`solution-${solution.id}`}
                >
                  {cardContent}
                </a>
              );
            }
            
            return (
              <div
                key={solution.id}
                className={cardClasses}
                data-testid={`solution-${solution.id}`}
              >
                {cardContent}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
